import {
  number,
  shape,
  string,
  arrayOf,
  objectOf,
  oneOfType,
  bool,
  oneOf,
  func
} from 'prop-types';
import { SurveyType } from 'unihow-common';
import {
  authMethods
} from '.';

export { SurveyType as surveyTypeValues };

export const variableTypeValues = {
  RELATIVE: 'RELATIVE',
  ABSOLUTE: 'ABSOLUTE'
};

export const questionTypeValues = {
  LIKERT: 'LIKERT',
  TEXT_FIELD: 'TEXT_FIELD',
  RADIO_BUTTON_GROUP: 'RADIO_BUTTON_GROUP',
  CHECKBOX_GROUP: 'CHECKBOX_GROUP',
  DROPDOWN: 'DROPDOWN'
};

export const staticTextTypeValues = {
  HEADING: 'HEADING',
  INSTRUCTION_TEXT: 'INSTRUCTION_TEXT'
};

export const schemaPartItemTypeValues = {
  STATIC_TEXT: 'STATIC_TEXT',
  QUESTION: 'QUESTION',
  QUESTION_GROUP: 'QUESTION_GROUP'
};

export const publicationStatusValues = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED'
};

export const surveyPartTypeValues = {
  EXTRA_SURVEY_PART: 'EXTRA_SURVEY_PART',
  SURVEY_SCHEMA_PART: 'SURVEY_SCHEMA_PART'
};

export const messageTypeValues = {
  INFO: 'INFO',
  ERROR: 'ERROR'
};

export const relativeCategoryTypeValues = [
  'LESS_THAN_ONE_STANDARD_DEVIATION_BELOW_MEAN',
  'WITHIN_ONE_STANDARD_DEVIATION_OF_MEAN',
  'GREATER_THAN_ONE_STANDARD_DEVIATION_ABOVE_MEAN'
];

export const participantIdTypeValues = {
  STUDENT_NUMBER: 'studentNumber',
  EXTERNAL_ID: 'externalId',
  EMAIL: 'email'
};

export const notificationType = {
  INFO: 'INFO',
  ALERT: 'ALERT',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  OK: 'OK'
};

export const localizedType = objectOf(string);

export const absoluteCategoryType = shape({
  description: localizedType.isRequired,
  value: number.isRequired
});

export const surveyScheduleType = shape({
  startDate: string.isRequired,
  endDate: string.isRequired,
  started: string,
  ended: string
});

export const surveyReminderType = shape({
  id: number.isRequired,
  scheduledTime: string.isRequired,
  sent: bool.isRequired
});

export const degreeProgramType = shape({
  code: string.isRequired,
  name: localizedType,
  groupId: string
});

export const surveyInfoType = shape({
  title: localizedType.isRequired,
  id: number.isRequired,
  code: string.isRequired,
  openQuestionResponseCount: number.isRequired,
  responseCount: number.isRequired,
  participantCount: number.isRequired,
  type: oneOf(Object.values(SurveyType)).isRequired,
  degreeProgram: degreeProgramType
});

export const surveyLinkType = shape({
  title: localizedType.isRequired,
  id: number.isRequired,
  code: string.isRequired,
  type: oneOf(Object.values(SurveyType)),
  schedule: surveyScheduleType
});

export const responseOptionType = shape({
  id: number.isRequired,
  value: oneOfType([number, string]).isRequired,
  text: localizedType.isRequired
});

export const responseOptionEditorType = shape({
  id: number,
  value: oneOfType([number, string]).isRequired,
  text: localizedType.isRequired
});

export const questionType = shape({
  id: number.isRequired,
  type: oneOf(Object.values(questionTypeValues)).isRequired,
  code: string.isRequired,
  text: localizedType.isRequired,
  required: bool.isRequired,
  responseOptions: arrayOf(responseOptionType)
});

export const questionEditorType = shape({
  id: number,
  type: oneOf(Object.values(questionTypeValues)).isRequired,
  code: string.isRequired,
  text: localizedType.isRequired,
  required: bool.isRequired,
  responseOptions: arrayOf(responseOptionEditorType)
});

export const questionGroupType = shape({
  id: number,
  responseOptions: arrayOf(responseOptionType).isRequired,
  questions: arrayOf(questionType).isRequired
});

export const questionGroupEditorType = shape({
  id: number,
  responseOptions: arrayOf(responseOptionEditorType).isRequired,
  questions: arrayOf(questionEditorType).isRequired
});

export const staticTextType = shape({
  type: oneOf(Object.values(staticTextTypeValues)).isRequired,
  text: localizedType.isRequired
});

export const SurveySchemaEditorType = shape({
  id: number,
  title: localizedType,
  code: string,
  status: oneOf(Object.values(publicationStatusValues)).isRequired,
  type: oneOf(Object.values(SurveyType)).isRequired,
  description: localizedType,
  introduction: localizedType,
  conclusion: localizedType,
  feedbackIntroduction: localizedType
});

export const surveySchemaLinkType = shape({
  title: localizedType.isRequired,
  id: number.isRequired,
  code: string.isRequired,
  status: oneOf(Object.values(publicationStatusValues)).isRequired,
  lockedFromEditing: bool.isRequired
});

export const SurveySchemaPartItemType = shape({
  id: number.isRequired,
  type: oneOf(Object.values(schemaPartItemTypeValues)).isRequired,
  question: questionType,
  questionGroup: questionGroupType,
  staticText: staticTextType
});

export const SurveySchemaPartItemEditorType = shape({
  id: number,
  type: oneOf(Object.values(schemaPartItemTypeValues)).isRequired,
  question: questionEditorType,
  questionGroup: questionGroupEditorType,
  staticText: staticTextType,
  order: number
});

export const surveySchemaMetadataType = shape({
  id: number.isRequired,
  code: string.isRequired,
  status: oneOf(Object.values(publicationStatusValues)).isRequired,
  title: localizedType,
  description: localizedType,
  version: number.isRequired
});

export const surveySchemaPartType = shape({
  id: number.isRequired,
  code: string.isRequired,
  version: number.isRequired,
  status: oneOf(Object.values(publicationStatusValues)).isRequired,
  lockedFromEditing: bool,
  title: localizedType,
  description: localizedType,
  surveySchemaPartItems: arrayOf(SurveySchemaPartItemType).isRequired,
  lastModifiedBy: string,
  lastModifiedDate: string,
  type: oneOf(Object.values(surveyPartTypeValues)).isRequired
});

export const variableExtraInfoType = shape({
  id: number.isRequired,
  surveyPartCode: string.isRequired,
  variableCode: string.isRequired,
  title: localizedType,
  info: localizedType
});

export const surveyPartUsageType = shape({
  surveySchemas: arrayOf(surveySchemaLinkType).isRequired,
  surveys: arrayOf(surveyLinkType).isRequired,
  lockedFromEditing: bool.isRequired
});

export const relativeCategoryType = shape({
  type: oneOf(Object.values(relativeCategoryTypeValues)).isRequired
});

export const responseCategoryType = shape({
  relativeCategory: relativeCategoryType,
  absoluteCategory: absoluteCategoryType,
  feedback: localizedType.isRequired,
  improvementSuggestions: localizedType.isRequired
});

export const variableEditorType = shape({
  id: number,
  code: string.isRequired,
  order: number.isRequired,
  name: localizedType.isRequired,
  description: localizedType.isRequired,
  questions: arrayOf(questionEditorType).isRequired,
  responseCategories: arrayOf(responseCategoryType).isRequired
});

export const SurveySchemaPartEditorType = shape({
  id: number,
  type: oneOf(Object.values(surveyPartTypeValues)).isRequired,
  surveyType: oneOf(Object.values(SurveyType)).isRequired,
  code: string.isRequired,
  title: localizedType,
  description: localizedType,
  surveySchemaPartItems: arrayOf(SurveySchemaPartItemEditorType).isRequired,
  variables: arrayOf(variableEditorType)
});

export const newSurveyType = shape({
  code: string,
  type: oneOf(Object.values(SurveyType)),
  degreeProgram: degreeProgramType,
  title: localizedType,
  introduction: localizedType,
  conclusion: localizedType,
  invitationEmailTitle: localizedType,
  invitationEmailContent: localizedType,
  researchAgreement: localizedType,
  researchConsentNeeded: bool,
  surveySchemaParts: arrayOf(surveySchemaPartType)
});

export const questionStatsType = shape({
  average: number.isRequired,
  standardDeviation: number.isRequired,
  distribution: objectOf(number).isRequired
});

export const variableStatsType = shape({
  type: oneOf(Object.values(variableTypeValues)).isRequired,
  responseOptionValues: arrayOf(number).isRequired,
  absoluteCategories: arrayOf(absoluteCategoryType),
  questionCodes: arrayOf(string),
  name: localizedType.isRequired,
  description: localizedType,
  average: number.isRequired,
  averageTotal: number.isRequired,
  distribution: objectOf(number).isRequired,
  userAverage: number,
  userTotal: number,
  minTotal: number.isRequired,
  maxTotal: number.isRequired,
  feedback: localizedType,
  improvementSuggestions: localizedType,
  order: number.isRequired,
  minAverage: number,
  maxAverage: number,
  summaryDescription: localizedType
});

export const surveyStatsType = shape({
  survey: newSurveyType.isRequired,
  responseCount: number.isRequired,
  participantCount: number,
  statisticsByQuestionId: objectOf(questionStatsType),
  variableStatistics: arrayOf(variableStatsType),
  feedbackIntroduction: localizedType,
  computingStatistics: bool,
  userHasSeenFeedback: bool,
  responseTime: string
});

export const surveyType = shape({
  id: number,
  type: oneOf(Object.values(SurveyType)),
  degreeProgram: degreeProgramType,
  courseRealisationIds: arrayOf(string),
  title: localizedType,
  description: localizedType,
  introduction: localizedType,
  conclusion: localizedType,
  researchAgreement: localizedType,
  researchConsentNeeded: bool,
  surveySchemaParts: arrayOf(surveySchemaPartType),
  extraSurveyPart: surveySchemaPartType,
  invitationEmailTitle: localizedType,
  invitationEmailContent: localizedType
});

export const participantType = shape({
  userId: number,
  externalId: string,
  studentNumber: string,
  employeeNumber: string,
  name: string,
  email: string,
  canEditEmail: bool
});

export const courseUnitRealisationType = shape({
  id: string,
  code: string,
  courseName: localizedType,
  startDate: string,
  endDate: string,
  enrolled: arrayOf(participantType)
});

export const surveyOptionsType = shape({
  id: number,
  schemaId: number,
  surveyType: oneOf(Object.values(SurveyType)),
  degreeProgram: degreeProgramType,
  courses: arrayOf(courseUnitRealisationType),
  courseRealisationIds: arrayOf(string),
  participants: arrayOf(participantType).isRequired,
  title: localizedType,
  description: localizedType,
  researchConsentNeeded: bool.isRequired,
  extraSurveyPart: surveySchemaPartType,
  reminderSchedules: arrayOf(string).isRequired
});

export const messageType = shape({
  id: number.isRequired,
  details: arrayOf(shape({
    code: number,
    url: string,
    message: string.isRequired
  })),
  text: string.isRequired,
  messageType: oneOf(Object.values(messageTypeValues)).isRequired
});

export const permissionType = shape({
  permission_name: string.isRequired,
  permission_type: string.isRequired,
  write_permission: bool.isRequired
});

export const currentUserType = shape({
  name: string.isRequired,
  organization: shape({
    name: string.isRequired,
    auth: oneOf(Object.values(authMethods))
  }),
  permissions: arrayOf(permissionType).isRequired,
  preferredLanguage: string.isRequired
});

export const previousLocationType = shape({
  pathname: string
});

export const langType = {
  lang: string.isRequired,
  translate: func.isRequired
};

export const selectOptionType = shape({
  value: oneOfType([string, number, bool]).isRequired,
  label: string.isRequired
});
